import type { AeaExerciseModel } from "p6m-p6u";
import { useEffect, useState } from "react";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useRTContext } from "../context/RTContext";
import { useTestCreationContext } from "../context/TestCreationContext";

const usePreviouslyUsedCards = (bandUuid: string | undefined) => {
    const { getRawVocabTrainings, schoolClasses, currentExerciseSchoolClassId } = useRTContext();
    const { creatingForSchoolClassId } = useGeneralContext();
    const { setPreviouslyUsedCardsIds, setPreviouslyUsedCardsClassName } = useTestCreationContext();

    // The class the training will be saved for: the header selection, or the class the creation was
    // started from (e.g. class page → new activity), mirroring saveUserExercise's groupId resolution.
    const effectiveSchoolClassId = currentExerciseSchoolClassId || creatingForSchoolClassId;

    const [assignedTrainings, setAssignedTrainings] = useState<AeaExerciseModel[]>([]);

    // Fetch the trainings assigned to this content. Reset first so a slow request for a previous
    // band can never leave its data on screen after switching to a new band (or clearing it).
    useEffect(() => {
        setAssignedTrainings([]);
        if (!bandUuid) return;

        let cancelled = false;

        async function load() {
            try {
                const trainings = await getRawVocabTrainings();
                if (cancelled) return;
                setAssignedTrainings(
                    trainings.filter((training) => !!training.groupId && training.subjectContentId?.id === bandUuid)
                );
            } catch {
                // Leave previously used state untouched on network failure
            }
        }

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bandUuid]);

    // Derive the used-card ids and the class name from the fetched trainings, scoped to the class
    // currently set in the creation header. Runs again when that class or the classes list changes so
    // the markers stay current without re-fetching the trainings.
    useEffect(() => {
        // Without a band there is nothing to derive — and writing the empty result would wipe
        // previously-used state another screen owns (e.g. SelectOwnContent in TEST mode mounts this
        // hook with no band).
        if (!bandUuid) return;

        if (!effectiveSchoolClassId) {
            setPreviouslyUsedCardsIds([]);
            setPreviouslyUsedCardsClassName("");
            return;
        }

        const usedCardIds = new Set<string>();
        for (const training of assignedTrainings) {
            if (training.groupId !== effectiveSchoolClassId) continue;

            for (const card of training.cardIds ?? []) {
                if (card.id) usedCardIds.add(card.id);
            }
        }

        setPreviouslyUsedCardsIds(Array.from(usedCardIds));
        setPreviouslyUsedCardsClassName(schoolClasses.find((c) => c.token === effectiveSchoolClassId)?.name ?? "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTrainings, schoolClasses, bandUuid, effectiveSchoolClassId]);
};

export default usePreviouslyUsedCards;
